import type {CopilotChatOrg} from '@github-ui/copilot-chat-types'
import {useSafeChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {createContext, type RefCallback, use, useEffect, useId, useMemo} from 'react'

type ReportConversationRefused = (reporterId: string, refused: boolean) => void

/**
 * Lets a transcript tell its host that the conversation was refused, so the host
 * can withdraw the chat input. Hosts that render no input can leave it unset.
 */
export const ConversationAccessContext = createContext<ReportConversationRefused | null>(null)

/**
 * Reports whether the transcript for `taskId` was refused. Each transcript
 * reports under its own key, so sessions of one task can't overwrite each
 * other's refusal, and withdraws its report when it unmounts.
 */
export function useReportConversationRefused(taskId: string | undefined, refused: boolean) {
  const report = use(ConversationAccessContext)
  const reporterId = useId()
  useEffect(() => {
    if (!report || !taskId) return
    report(reporterId, refused)
    return () => report(reporterId, false)
  }, [report, reporterId, taskId, refused])
}

/**
 * SSO organizations for hosts that render a transcript outside a
 * CopilotChatProvider. The chat provider's `ssoOrganizations` wins when present.
 */
export const SsoOrganizationsContext = createContext<CopilotChatOrg[] | undefined>(undefined)

/**
 * Registers a rendered locked state with its host, so the host can move focus to
 * it when the lock replaces a chat input that had focus.
 */
export const ConversationLockedFocusContext = createContext<RefCallback<HTMLDivElement> | null>(null)

const EMPTY: string[] = []

/**
 * Logins of the viewer's organizations whose SSO session has lapsed.
 *
 * This is every such organization, not only the ones a conversation reached:
 * Mission Control doesn't say which organization closed a conversation.
 */
export function useLapsedSsoOrgLogins(explicit?: CopilotChatOrg[]): string[] {
  const chatState = useSafeChatState()
  const fromContext = use(SsoOrganizationsContext)
  const organizations = explicit ?? chatState?.ssoOrganizations ?? fromContext
  return useMemo(() => {
    if (!organizations?.length) return EMPTY
    return organizations.map(org => org.login).filter((login): login is string => !!login)
  }, [organizations])
}
